import React, { useContext, useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Landmark, Plus, Trash2, ShieldCheck, Sparkles, Crown, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { GateContext } from "@/context/GateContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PinPad } from "@/components/PinPad";
import { FileDown } from "lucide-react";

const CATEGORIES = ["Direct", "Consequential", "Legal"];
const VERIFICATIONS = ["Receipt", "Bank Log", "Invoice"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ProBadge() {
  return <span className="ek-stamp" data-testid="pro-badge" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.45)" }}>PRO</span>;
}

function PremiumGate({ onUpgrade }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto" data-testid="damages-premium-gate">
      <div className="border rounded-xl p-8 text-center space-y-5" style={{ background: "rgba(46,130,246,0.06)", borderColor: "rgba(46,130,246,0.3)" }}>
        <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "rgba(46,130,246,0.12)" }}>
          <Landmark className="h-7 w-7" style={{ color: "var(--ek-primary)" }} />
        </div>
        <div className="flex items-center justify-center gap-2">
          <h2 className="font-head text-2xl font-semibold tracking-tight">Financial Damages Ledger</h2>
          <ProBadge />
        </div>
        <div className="rounded-lg border px-4 py-3 text-left flex gap-3" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--ek-primary)" }} />
          <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }} data-testid="justice-conversion-msg">
            "I can help you build a professional <b style={{ color: "var(--ek-text)" }}>Statement of Damages</b> for court —
            itemized, sanitized, and Bates-numbered. This feature is available to our <b style={{ color: "var(--ek-text)" }}>Litigator Pro</b> members."
          </p>
        </div>
        <Button onClick={onUpgrade} data-testid="upgrade-to-pro-btn" className="rounded-md w-full sm:w-auto" style={{ background: "var(--ek-primary)", color: "#fff" }}>
          <Crown className="h-4 w-4 mr-2" /> Upgrade to Litigator Pro · $44.99/mo
        </Button>
      </div>
    </motion.div>
  );
}

export default function FinancialLedger({ matterId, proAccess, onUpgrade, user, onSetupPin }) {
  const { gate } = useContext(GateContext);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total: 0, out_of_pocket: 0, unpaid_owed: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ date: "", amount: "", category: "Direct", verification_type: "Receipt", recovery_status: "Out-of-Pocket", description: "" });
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [statement, setStatement] = useState(null);

  const load = useCallback(async () => {
    if (!proAccess) return;
    try {
      const { data } = await api.get("/financial/entries", { params: matterId ? { matter_id: matterId } : {} });
      setEntries(data.entries); setTotals(data.totals);
    } catch { /* gated or empty */ }
  }, [proAccess, matterId]);

  useEffect(() => { load(); }, [load]);

  if (!proAccess) return <PremiumGate onUpgrade={onUpgrade} />;

  const reset = () => setForm({ date: "", amount: "", category: "Direct", verification_type: "Receipt", recovery_status: "Out-of-Pocket", description: "" });

  const save = async () => {
    if (!gate()) return;
    if (!form.description.trim()) { toast.message("Add a description first"); return; }
    if (!(parseFloat(form.amount) > 0)) { toast.message("Enter an amount greater than $0"); return; }
    setBusy(true);
    try {
      await api.post("/financial/entries", {
        matter_id: matterId,
        date: form.date ? new Date(form.date).toISOString() : null,
        amount: parseFloat(form.amount) || 0,
        category: form.category,
        verification_type: form.verification_type,
        recovery_status: form.recovery_status,
        description: form.description,
      });
      toast.success("Justice sanitized and logged this damage");
      setOpen(false); reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save entry"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/financial/entries/${id}`); toast.success("Entry removed"); load(); }
    catch { toast.error("Could not remove entry"); }
  };

  const startExport = () => {
    if (!gate()) return;
    if (entries.length === 0) { toast.message("Add a financial entry first"); return; }
    if (!user?.has_vault_pin) { onSetupPin && onSetupPin(); return; }
    setPin(""); setPinError(""); setStatement(null); setPinOpen(true);
  };

  const doExport = async () => {
    if (pin.length !== 6) { setPinError("Enter your 6-digit PIN"); return; }
    setExporting(true); setPinError(""); setStatement(null);
    try {
      const { data } = await api.post("/financial/statement", { matter_id: matterId, case_number: "", pin });
      setStatement(data);
      setPinOpen(false);
      toast.success("Statement of Damages ready");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch (e) {
      setPinError(e.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="damages-ledger">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="ek-stamp inline-block">LITIGATOR PRO</div><ProBadge />
          </div>
          <h2 className="font-head text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Landmark className="h-6 w-6" style={{ color: "var(--ek-primary)" }} /> Damages Ledger
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>Itemized, sanitized financial damages for your Statement of Damages.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={startExport} data-testid="export-damages-btn" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            <FileDown className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Export Damages Statement
          </Button>
          <Button onClick={() => gate(() => setOpen(true))} data-testid="add-financial-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            <Plus className="h-4 w-4 mr-2" /> Add financial entry
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: "TOTAL CLAIMED", value: totals.total, tid: "total-claimed" },
          { label: "OUT-OF-POCKET", value: totals.out_of_pocket, tid: "total-out-of-pocket" },
          { label: "UNPAID OWED", value: totals.unpaid_owed, tid: "total-unpaid-owed" },
        ].map((c) => (
          <div key={c.tid} data-testid={c.tid} className="border rounded-lg px-4 py-3" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
            <div className="ek-mono-label">{c.label}</div>
            <div className="font-head text-xl font-semibold mt-1" style={{ color: "var(--ek-primary)" }}>{money(c.value)}</div>
          </div>
        ))}
      </div>

      {entries.length === 0 ? (
        <div className="border rounded-lg py-12 text-center" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="financial-empty">
          <Receipt className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--ek-steel)" }} strokeWidth={1.4} />
          <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>No financial damages logged yet.</p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="financial-list">
          {entries.map((e) => (
            <div key={e.id} className="p-4 flex items-start justify-between gap-3" data-testid={`financial-row-${e.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="ek-stamp" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.35)" }}>{e.category.toUpperCase()}</span>
                  <span className="ek-stamp" style={{ color: e.recovery_status === "Out-of-Pocket" ? "var(--ek-secure)" : "var(--ek-warning)", borderColor: "var(--ek-border)" }}>{e.recovery_status.toUpperCase()}</span>
                  <span className="ek-mono-label">{(e.date || "").slice(0, 10)} · {e.verification_type}</span>
                </div>
                <p className="text-sm" style={{ color: "var(--ek-text)" }} data-private>{e.description}</p>
                {e.raw_description && e.raw_description !== e.description && (
                  <p className="text-xs mt-1" style={{ color: "var(--ek-text-muted)" }} data-private>Original: {e.raw_description}</p>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-head font-semibold" style={{ color: "var(--ek-primary)" }}>{money(e.amount)}</div>
                <button onClick={() => remove(e.id)} data-testid={`delete-financial-${e.id}`} className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--ek-error)" }}>
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs" style={{ color: "var(--ek-text-muted)" }}>
        <ShieldCheck className="h-3.5 w-3.5" /> Descriptions are auto-sanitized into neutral accounting language for court.
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-head font-semibold tracking-tight">Add financial damage</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Justice will sanitize your description into neutral accounting terms.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" data-testid="financial-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-transparent" />
              </div>
              <div className="space-y-2">
                <Label>Amount (USD)</Label>
                <Input type="number" step="0.01" min="0" data-testid="financial-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-transparent" placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Transaction category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="financial-category-select" className="bg-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-opt-${c}`}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Verification type</Label>
                <Select value={form.verification_type} onValueChange={(v) => setForm({ ...form, verification_type: v })}>
                  <SelectTrigger data-testid="financial-verification-select" className="bg-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>{VERIFICATIONS.map((c) => <SelectItem key={c} value={c} data-testid={`verification-opt-${c}`}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Recovery status</Label>
              <div className="flex gap-2">
                {["Out-of-Pocket", "Unpaid Owed"].map((s) => (
                  <button key={s} type="button" data-testid={`recovery-${s === "Out-of-Pocket" ? "paid" : "owed"}`} onClick={() => setForm({ ...form, recovery_status: s })}
                    className="ek-stamp" style={{ background: form.recovery_status === s ? "var(--ek-surface-hover)" : "transparent", color: form.recovery_status === s ? "var(--ek-primary)" : "var(--ek-steel)", borderColor: form.recovery_status === s ? "rgba(46,130,246,0.45)" : "var(--ek-border)" }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid="financial-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-transparent resize-none" placeholder="Describe the loss in your own words — Justice will neutralize the language." />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy || !form.description.trim()} data-testid="save-financial-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {busy ? "SANITIZING…" : "Sanitize & save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="damages-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-semibold tracking-tight">Authorize export</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Enter your 6-digit Vault PIN to generate the Statement of Damages.</DialogDescription>
          </DialogHeader>
          <div className="py-4 flex flex-col items-center gap-3">
            <PinPad value={pin} onChange={setPin} testid="damages-pin-input" autoFocus />
            {pinError && <p className="text-sm" style={{ color: "var(--ek-error)" }} data-testid="damages-pin-error">{pinError}</p>}
          </div>
          <DialogFooter>
            <Button onClick={doExport} disabled={exporting} data-testid="confirm-damages-export-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {exporting ? "GENERATING…" : "Generate statement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
